import * as React from 'react'

import { Box, Button, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import copy from 'copy-to-clipboard'
import { Field } from 'factorial-form'
import { FormikProps } from 'formik'
import { UpdateAttendanceSettingMutationVariables } from 'generated/resources/graphql'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import InputText from 'modules/core/components/Inputs/Text'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { Return } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import Company from 'modules/core/store/models/Company'
import app from 'modules/core/store/models/app'

// TODO: importing private component
import ActivateCompanySlug from 'modules/app_store/components/ActivateAddonModal/ActivateCompanySlug'
import { ActivatePayload } from 'modules/app_store/lib/SubscriptionAddonInstaller'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

type TouchableProps = React.ComponentProps<typeof Button>

interface Props {
  attendanceSetting: AttendanceSetting
  policyForm: FormikProps<UpdateAttendanceSettingMutationVariables>
  updatePolicy: (value: { successFlash: boolean }) => Promise<Return<void>>
}

const EntranceAppCheckbox = ({ attendanceSetting, policyForm, updatePolicy }: Props) => {
  const { company } = useCurrentSession()
  const qrCodeClockin = policyForm.values.qrCodeClockin
  const companyId = company.id

  const publicToken = attendanceSetting.publicToken
  const completeURL = company.publicUrl?.concat(`/entrance?token=${String(publicToken)}`) ?? ''

  const { running: loading, performAction: handleResetAttendanceSecretKey } = useAsyncAction(
    async () => {
      const currentCompany = new Company({ id: companyId })
      await currentCompany.resetAttendanceSecretKeyRpc()
    },
    {
      successMessage: i18n.t('time_tracking.settings.success_message_reset_url'),
      errorMessage: i18n.t('time_tracking.settings.error_message_reset_url'),
    }
  )

  const handleResetQRCode = async () => {
    const data = await handleResetAttendanceSecretKey()
    if (data.success) {
      await attendanceSetting.fetch()
    }
  }

  const [slugModal, setSlugModal] = React.useState<boolean>(!company.slug)

  const copyURL: TouchableProps['onClick'] = (event) => {
    event.preventDefault()
    publicToken && copy(completeURL)
    app.setFlash({
      type: 'correct',
      message: i18n.t('time_tracking.settings.success_copy_url'),
    })
  }

  const checkForSlug = (isActive: boolean) => {
    policyForm.setFieldValue('qrCodeClockin', isActive)
    if (isActive) {
      policyForm.setValues({
        ...policyForm.values,
        qrCodeClockin: true,
        geolocation: false,
        geofencing: false,
        geolocationBlock: false,
      })
      !publicToken && setSlugModal(true)
    } else {
      policyForm.setValues({
        ...policyForm.values,
        qrCodeClockin: false,
      })
    }
    updatePolicy({
      successFlash: false,
    })
  }

  const onSuccessSlug: (
    payload?: ActivatePayload | null | undefined
  ) => Promise<boolean> = async () => {
    setSlugModal(false)
    return true
  }

  const onCloseSlug = async () => {
    // If the policy enabled qr_code_clockin but the slug was not set,
    // and now the user closed the slug modal without setting any slug,
    // the qr_code_clockin is disabled.
    setSlugModal(false)

    if (qrCodeClockin && !company.slug) {
      policyForm.setValues({
        ...policyForm.values,
        qrCodeClockin: false,
      })

      updatePolicy({
        successFlash: false,
      })
    }
  }

  return (
    <Box
      gap="s12"
      extra={{ 'data-intercom-target': 'attendance-policy-details-toggles-entrance-app' }}
    >
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Text.Normal weight="medium">
          {i18n.t('settings.customizations.attendance.entrance_app_checkbox_label')}
        </Text.Normal>
        <TogglerCheckbox value={!!qrCodeClockin} onChange={checkForSlug} />
      </Box>
      <Text.Normal color="grey800" htmlTag="div">
        {i18n.t('time_tracking.settings.form.toggle.qr.description')}
      </Text.Normal>
      {publicToken && qrCodeClockin && (
        <Box gap="s16">
          <InputText
            readonly
            label={i18n.t('settings.customizations.attendance.entrance_app_url')}
            field={new Field(completeURL, 'string')}
          />
          <Box gap="s8" flexDirection="row">
            <Button onClick={copyURL} type="secondary">
              {i18n.t('settings.customizations.attendance.entrance_app_copy_title')}
            </Button>
            <Button type="primary" disabled={loading} onClick={handleResetQRCode}>
              {i18n.t('settings.customizations.attendance.generate_new_entrance_app_link')}
            </Button>
          </Box>
          <Text size="100">
            {i18n.t('settings.customizations.attendance.entrance_app_description_small')}
          </Text>
          <HelpCenterLink name="qr_code_clock_in">
            {i18n.t('time_tracking.settings.form.toggle.qr.link')}
          </HelpCenterLink>
        </Box>
      )}
      {slugModal && <ActivateCompanySlug onSuccess={onSuccessSlug} onClose={onCloseSlug} />}
    </Box>
  )
}

export default EntranceAppCheckbox
